import { CorrelationTableConfig, useCorrelationTableConfig } from "@pages/Correlation/CorrelationTableConfig";
import { FCX, includesInLC, startsFromInLC } from "@datassential/platform-ui-lib";
import { memo, ReactNode, useMemo } from "react";
import cn from "classnames";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { TableColumnsContext } from "@components/Table/TableColumns";
import { VirtualizedTable } from "@components/Table";
import { getCopyTableData } from "@components/Table/TableUtils";
import { Sort } from "@models";
import { useFilterSort } from "@hooks/useFilterSort";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import { CorrelationItem, CorrelationResponse } from "@api/useCorrelation";
import Loader from "@components/Loader";
import { Metric } from "@models/Metric";
import { useSelectedKeywordsContext } from "@vms/SelectedKeywords/SelectedKeywordsContext";

const CorrelationTable: FCX<{
    search: string;
    mainKeywordId: number;
    isLoading: boolean;
    rawApiData: CorrelationResponse;
    selectedCorrelatingMetric: Metric | null;
    selectedHeadline: ReactNode;
    relatingHeadline: string;
}> = ({
    search,
    mainKeywordId,
    isLoading,
    rawApiData,
    selectedCorrelatingMetric,
    selectedHeadline,
    relatingHeadline,
    className,
    ...props
}) => {
    const { getMatchFilters } = useSelectFilters();
    const {
        selectedKeywordsIds,
        isSelectedKeywordsView,
    } = useSelectedKeywordsContext();

    const isSearchApplied = useMemo(
        () => search.trim().length > 0,
        [search]
    );

    const rawData = useMemo(() => rawApiData.tableData, [rawApiData])

    const preSortedData = useMemo(
        () => [...rawData].sort(
            (a, b) => (a.name ?? '').localeCompare(b.name ?? '')
        ),
        [rawData]
    );

    const mainKeyword = useMemo(
        () => rawData.find(i => i.id === mainKeywordId)!,
        [rawData, mainKeywordId]
    );

    const selectedKeywords = useMemo(
        () => rawData.filter(i => selectedKeywordsIds.includes(i.id ?? 0)),
        [rawData, selectedKeywordsIds]
    );

    const filteredData = useMemo(
        () => preSortedData.filter(
            i => {
                let result = true;

                if (i.id === mainKeywordId) return false;

                // If correlating metric is not selected, don't show any keywords
                if (!selectedCorrelatingMetric) return false;

                if (isSearchApplied) {
                    result = includesInLC(i.name ?? '', search.trim());
                }

                result = result && getMatchFilters(i);

                return result;
            }
        ),
        [mainKeywordId, selectedCorrelatingMetric, preSortedData, isSearchApplied, search, getMatchFilters]
    );

    const viewData = useMemo(
        () => isSelectedKeywordsView ? selectedKeywords : filteredData,
        [isSelectedKeywordsView, selectedKeywords, filteredData]
    );

    const dataForSorting = useMemo(
        () => viewData
            .map(i => ({
                ...i,
                ...i.metrics.reduce(
                    (acc, metric) => ({
                        ...acc,
                        [`metric${metric.id}`]: metric.numValue,
                    }),
                    {}
                ),
            }))
        ,
        [viewData]
    );

    const sortedBySearch = useMemo(
        () => {
            if (!isSearchApplied) return filteredData;

            return [
                ...dataForSorting.filter(i => startsFromInLC(i.name ?? '', search.trim())),
                ...dataForSorting.filter(i => !startsFromInLC(i.name ?? '', search.trim())),
            ];
        },
        [isSearchApplied, filteredData, dataForSorting, search]
    );

    const metricSortKey = useMemo(
        () => `metric${selectedCorrelatingMetric}` as keyof CorrelationItem
        ,
        [selectedCorrelatingMetric]
    );

    const searchFields = useMemo(
        (): (keyof CorrelationItem)[] => ['name'],
        []
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        dataForSorting,
        metricSortKey,
        Sort.Desc,
        searchFields,
    );

    const data = useMemo(
        () => [
            mainKeyword,
            ...(isSearchApplied ? sortedBySearch : sortedData)
        ],
        [mainKeyword, isSearchApplied, sortedBySearch, sortedData]
    );

    const sortContextValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey as string,
                direction: sortingDirection,
            },
            options: [
                { id: "name", name: "name", defaultDirection: Sort.Asc },
                { id: "baseCount", name: "baseCount" },
                { id: metricSortKey, name: metricSortKey },
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof CorrelationItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection, metricSortKey]
    );

    const configPayload = useMemo(
        (): CorrelationTableConfig => ({
            data,
            search,
            selectedCorrelatingMetric,
            selectedHeadline,
            relatingHeadline,
        }), [
            data,
            search,
            selectedCorrelatingMetric,
            selectedHeadline,
            relatingHeadline,
        ]
    );

    const tableConfig = useCorrelationTableConfig(configPayload);
    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig]
    );

    const copyTableContent = useMemo(
        () => getCopyTableData(tableProviderValue.data, data.length),
        [tableProviderValue.data, data.length]
    );

    const tableKey = useMemo(() => {
        return `${data.length}-${Date.now()}`;
    }, [data]);

    const hasInsufficientBaseSizeKeywords = useMemo(() => {
        // We have only one keyword data which is the selected keyword
        return !isLoading && rawApiData.tableData.length === 1 && rawApiData.tableData[0].id === mainKeywordId;
    }, [isLoading, rawApiData, mainKeywordId])

    const hasNoItems = useMemo(() => {
        return !isLoading && !hasInsufficientBaseSizeKeywords && selectedCorrelatingMetric && data.length === 1 && data[0].id === mainKeywordId;
    }, [isLoading, hasInsufficientBaseSizeKeywords, data, mainKeywordId, selectedCorrelatingMetric])

    const tableChildren = useMemo(() => {
        return (
            <>
                {
                    hasInsufficientBaseSizeKeywords ? (
                        <span className="CorrelationTable__no-items">No other keywords meet the minimum base size for analysis,<br/>please try another metric or keyword</span>
                    ) : null
                }
                {
                    hasNoItems ? (
                        <span className="CorrelationTable__no-items">No matching items found</span>
                    ) : null
                }
            </>
        )
    }, [hasInsufficientBaseSizeKeywords, hasNoItems])

    if (isLoading) {
        return <Loader className="CorrelationTable appears"/>
    }

    return (
        <div className="CorrelationTable appears-top del-2">
            <AsideButtons className="CorrelationTable__aside-buttons">
                <CopyTableButton
                    data={copyTableContent}
                    testId="buttonCorrelationCopyTable"
                />
            </AsideButtons>
            <TableSortingContext.Provider value={sortContextValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <div
                        className={cn("CorrelationTable__container", className)}
                        {...props}
                    >
                        <VirtualizedTable
                            key={tableKey}
                            className="CorrelationTable__table"
                            headHeight={48}
                            rowHeight={42}
                            rowsCount={data.length}
                            stickyRowsCount={1}
                            testId="tableCorrelationVirtualized"
                            useContentHeight
                        >
                            {tableChildren}
                        </VirtualizedTable>
                    </div>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
        </div>
    )
}

export default memo(CorrelationTable);